// ============================================================================
// HidTransport — Generic WebHID send/receive wrapper with timeout
// ============================================================================

/**
 * Generic WebHID transport layer. Wraps a HIDDevice with typed
 * send/receive operations and configurable timeouts.
 *
 * Key design: `sendAndReceive` registers the `inputreport` listener
 * BEFORE calling `sendReport` to avoid race conditions where the
 * device responds before the listener is attached.
 */
export class HidTransport {
  private device: HIDDevice;
  private reportSize: number;

  constructor(device: HIDDevice, reportSize: number = 64) {
    this.device = device;
    this.reportSize = reportSize;
  }

  get opened(): boolean {
    return this.device.opened;
  }

  get vendorId(): number {
    return this.device.vendorId;
  }

  get productId(): number {
    return this.device.productId;
  }

  get productName(): string {
    return this.device.productName;
  }

  async open(): Promise<void> {
    if (!this.device.opened) {
      await this.device.open();
    }
  }

  async close(): Promise<void> {
    if (this.device.opened) {
      await this.device.close();
    }
  }

  /**
   * Send a report to the device. Data is zero-padded to reportSize.
   */
  async sendReport(data: Uint8Array, reportId: number = 0x00): Promise<void> {
    const padded = new Uint8Array(this.reportSize);
    padded.set(data.subarray(0, this.reportSize));
    await this.device.sendReport(reportId, padded);
  }

  /**
   * Send a report and wait for the response. The `inputreport` listener
   * is registered BEFORE sending to avoid race conditions.
   */
  async sendAndReceive(
    data: Uint8Array,
    timeoutMs: number = 5000,
    reportId: number = 0x00
  ): Promise<Uint8Array> {
    const responsePromise = this.waitForReport(timeoutMs);
    await this.sendReport(data, reportId);
    return responsePromise;
  }

  /**
   * Wait for the next input report from the device.
   */
  waitForReport(timeoutMs: number = 5000): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      let settled = false;

      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          this.device.removeEventListener('inputreport', onReport);
          reject(new Error(`HID report timeout after ${timeoutMs}ms`));
        }
      }, timeoutMs);

      const onReport = (event: HIDInputReportEvent) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          this.device.removeEventListener('inputreport', onReport);
          const result = new Uint8Array(event.data.buffer);
          resolve(result);
        }
      };

      this.device.addEventListener('inputreport', onReport);
    });
  }
}
